import { useMemo } from 'react';
import { getResolvedBuildingVersion } from '@shared/helpers/actions/resolved-version.helpers';
import {
  getElementById,
  isElement,
} from '@shared/helpers/recursive-element.helpers';
import type { IElement, OneOfElements } from '@shared/models/project.model';
import { ItemOrItemId } from '@shared/models/type-helpers.model';
import { getId } from '@shared/helpers/object.helpers';
import { getEmptyFrozenArray } from '@shared/helpers/array.helpers';
import { getProject } from '@client/store/project';
import { getSelectedVersion } from '@client/store/ui';
import { getDataTables } from '@features/data-table/store/data-table-store.hooks';
import { getPvgisState } from '@client/store/pvgis/pvgis.hook';
import { getSelectedProposalIdFromUrl } from './use-selected-proposal-id.hooks';
import { useResolvedElement } from './use-resolved-version.hooks';

/**
 * Proposal-resolved {@link IElement} for recipe UI (isDirty, save, detach, save as)
 * and for recipe preview/apply callbacks. Falls back to the passed baseline element
 * when no proposal is selected or the id is missing from the resolved tree.
 */
export const useRecipeContextElement = (
  elementOrId?: ItemOrItemId<OneOfElements>,
): IElement | undefined => {
  const id = elementOrId ? getId(elementOrId) : undefined;
  const resolved = useResolvedElement(id);

  return useMemo(() => {
    if (isElement(resolved)) return resolved;
    if (elementOrId && isElement(elementOrId)) return elementOrId;
    return undefined;
  }, [resolved, elementOrId]);
};

/**
 * Non-hook counterpart for store callbacks (e.g. {@link useApplyRecipe}).
 * Mirrors {@link useRecipeContextElement} using current version, URL proposal, and project enrich context.
 */
export const getRecipeContextElement = (element: IElement): IElement => {
  const version = getSelectedVersion(true);
  if (!version) return element;

  const proposalId = getSelectedProposalIdFromUrl();
  if (!proposalId) return element;

  const project = getProject();
  const enrichContext = project
    ? {
        project,
        weatherHours:
          getPvgisState().weatherData?.environmentHourData ??
          getEmptyFrozenArray(),
        dataTables: getDataTables(),
      }
    : undefined;

  const resolvedVersion = getResolvedBuildingVersion(
    version,
    proposalId,
    enrichContext,
  );
  const resolved = getElementById(resolvedVersion, element.id, false);
  return isElement(resolved) ? resolved : element;
};
